// @flow
import * as React from 'react'
import styled from 'styled-components'

import { classnames } from '@/helpers'

import styles from './styles.scss'

const getRect = (element: ?Element) => {
  if (!element) return {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
  }
  return element.getBoundingClientRect()
}

const Overlay = styled.div`
  max-width: ${props => props.maxWidth - 20}px;
`

type Props = {
  /* eslint-disable react/require-default-props */
  placement?: 'left' | 'right',
  trigger?: Array<'hover' | 'click' | 'focus'>,
  content: React.Node,
  children: React.Element<'div' | 'span' | 'p'>
  /* eslint-enable react/require-default-props */
}

const Tooltip = ({
  content,
  children,
  placement = 'left',
  trigger = ['hover', 'click'],
}: Props) => {
  const [visible, setVisible] = React.useState(false) 
  const [maxWidth, setMaxWidth] = React.useState(300)
  const ref = React.useRef<Element | typeof(undefined) | null>()
  const { props: childProps } = children

  const onVisibleChange = (value) => { setVisible(value) }

  const onMouseOver = (e: SyntheticMouseEvent<>) => {
    if (childProps.onMouseOver) childProps.onMouseOver(e)
    if (!trigger.includes('hover')) return
    onVisibleChange(!visible)
  }
  const onMouseLeave = (e: SyntheticMouseEvent<>) => {
    if (childProps.onMouseLeave) childProps.onMouseLeave(e)
    if (!trigger.includes('hover')) return
    onVisibleChange(false)
  }
  const onClick = (e: SyntheticMouseEvent<>) => {
    if (childProps.onClick) childProps.onClick(e)
    if (!trigger.includes('click')) return
    onVisibleChange(!visible)
  }
  const onFocus = (e: SyntheticMouseEvent<>) => {
    e.stopPropagation()
    if (childProps.onFocus) childProps.onFocus(e)
    if (!trigger.includes('focus')) return
    onVisibleChange(!visible)
  }

  const getPlacement = () => {
    const childRect = getRect(ref.current)
    
    if (placement === 'left' && (childRect.left + maxWidth) > window.innerWidth) {
      if (childRect.right - maxWidth < 0) {
        setMaxWidth(window.innerWidth - childRect.left)
        return 'left'
      }
      return 'right'
    }

    if (placement === 'right' && (childRect.right - maxWidth) < 0) {
      if (childRect.left + maxWidth > window.innerWidth) {
        setMaxWidth(childRect.right)
        return 'right'
      }
      return 'left'
    }

    return placement
  }

  const refChildren = React.cloneElement(children, {
    ref,
    className: classnames(childProps.className),
    onMouseOver,
    onMouseLeave,
    onClick,
    onFocus
  })

  return (
    <div className={styles.referable}>
      {refChildren}
      <Overlay
        className={
          visible
            ? classnames(styles.overlay, styles.visible, styles[getPlacement()])
            : classnames(styles.overlay, styles[getPlacement()])
        }
        maxWidth={maxWidth}
      >
        {content}
      </Overlay>
    </div>
  )
}

export default Tooltip
